import {
  MAX_TRACKED_RELATION_PATHS,
  MAX_WALK_DEPTH,
  MAX_WALK_NODES,
  OVERFLOW_RELATION_PATH,
  TOP_RELATION_COUNT,
} from "./constants";
import type { HydrationMeasurement, HydrationWalkOptions } from "./types";

/**
 * Count the objects a Prisma result actually hydrated, and attribute them to
 * the relations that produced them.
 *
 * The top-level `rowCount` the extension already logs counts only the outer
 * array, so a query returning 50 rows with a deep `include` can hydrate
 * hundreds of thousands of objects and look tiny. This walk closes that blind
 * spot.
 *
 * Three properties are non-negotiable, because the thing being measured is the
 * thing that kills the process:
 *
 * 1. **Never serialize.** `JSON.stringify` on a multi-GB result is the exact
 *    failure mode under investigation. Nothing here builds a string from the
 *    data; only relation *keys* are ever read as strings.
 * 2. **Hard bounds.** Total nodes visited is capped, and the traversal queue is
 *    capped with it (children are not enqueued once `visited + pending`
 *    reaches the cap, though already-pending nodes are still drained so the
 *    count stays useful), so peak extra allocation is O(maxNodes) pointers no
 *    matter how large the result is. Depth is capped independently. This also
 *    bounds the walk's *duration*, which is what the caller's rate limiter
 *    relies on to make the instrument's CPU cost a constant.
 * 3. **Truthful truncation.** Hitting either cap is reported. A clearly
 *    labelled lower bound beats an accurate number that OOMs the pod.
 *
 * The traversal is breadth-first specifically because of (2): when the cap
 * cuts a walk short, level-order means the sample spans every top-level row
 * and every relation at the depth reached, so `topRelations` stays
 * proportionally representative. Depth-first would spend the whole budget
 * inside the first row's subtree and attribute the fan-out to whichever
 * relation happened to be visited first.
 *
 * PHI: only object identity and key names are read. Scalar values — strings,
 * numbers, dates — are never read, copied, compared or logged.
 */
export const measureHydratedNodes = (
  result: unknown,
  {
    maxNodes = MAX_WALK_NODES,
    maxDepth = MAX_WALK_DEPTH,
    maxTrackedRelationPaths = MAX_TRACKED_RELATION_PATHS,
    topRelationCount = TOP_RELATION_COUNT,
  }: HydrationWalkOptions = {},
): HydrationMeasurement => {
  const relationNodeCounts = new Map<string, number>();
  let nodeCount = 0;
  let maxDepthReached = 0;
  let truncated = false;
  let depthCapped = false;

  if (result === null || typeof result !== "object") {
    return {
      nodeCount: 0,
      truncated: false,
      maxDepth: 0,
      depthCapped: false,
      topRelations: [],
    };
  }

  // Prisma results are trees, but a cycle would be unbounded, so guard anyway.
  const seen = new WeakSet<object>();
  // A queue drained by index rather than `shift()`, which would be O(n) per
  // node. `head` only advances, so the pending count is `queue.length - head`.
  const queue: _QueueEntry[] = [{ value: result, depth: 0, path: "" }];
  let head = 0;

  while (head < queue.length) {
    // Non-null: guarded by the loop condition, and nothing else advances head.
    const { value, depth, path } = queue[head] as _QueueEntry;
    head += 1;

    if (seen.has(value)) {
      continue;
    }
    seen.add(value);

    nodeCount += 1;
    if (depth > maxDepthReached) {
      maxDepthReached = depth;
    }
    if (path !== "") {
      _addRelationNodes(relationNodeCounts, path, maxTrackedRelationPaths);
    }

    if (nodeCount >= maxNodes) {
      truncated = true;
      break;
    }

    if (_isLeafObject(value)) {
      continue;
    }

    if (depth >= maxDepth) {
      depthCapped = true;
      continue;
    }

    const childDepth = depth + 1;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === null || typeof item !== "object") {
          continue;
        }
        if (nodeCount + (queue.length - head) >= maxNodes) {
          truncated = true;
          break;
        }
        // An array is a container, not a relation hop: its items keep the
        // path of the key that held the array.
        queue.push({ value: item, depth: childDepth, path });
      }
    } else {
      for (const key of Object.keys(value)) {
        const child = (value as Record<string, unknown>)[key];
        if (child === null || typeof child !== "object") {
          continue;
        }
        if (nodeCount + (queue.length - head) >= maxNodes) {
          truncated = true;
          break;
        }
        queue.push({
          value: child,
          depth: childDepth,
          path: path === "" ? key : `${path}.${key}`,
        });
      }
    }
  }

  return {
    nodeCount,
    truncated,
    maxDepth: maxDepthReached,
    depthCapped,
    topRelations: _topRelations(relationNodeCounts, topRelationCount),
  };
};

type _QueueEntry = { value: object; depth: number; path: string };

/**
 * Objects with no useful interior: descending into them would inflate the
 * count with engine internals rather than naming a relation. `Date` is the one
 * that matters — the OOM snapshot held 238,306 Prisma-hydrated dates — and
 * each still counts as one node, which is the point.
 */
const _isLeafObject = (value: object) =>
  value instanceof Date ||
  value instanceof ArrayBuffer ||
  ArrayBuffer.isView(value);

const _addRelationNodes = (
  counts: Map<string, number>,
  path: string,
  maxTrackedRelationPaths: number,
) => {
  const existing = counts.get(path);
  if (existing !== undefined) {
    counts.set(path, existing + 1);
    return;
  }

  if (counts.size >= maxTrackedRelationPaths) {
    counts.set(
      OVERFLOW_RELATION_PATH,
      (counts.get(OVERFLOW_RELATION_PATH) ?? 0) + 1,
    );
    return;
  }

  counts.set(path, 1);
};

const _topRelations = (counts: Map<string, number>, limit: number) =>
  [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([relation, nodeCount]) => ({ relation, nodeCount }));
